import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { TypedMemo } from '../common/types';
import { config } from '../../config/config';
import { Music24Logo } from '../music24/components/Music24Logo';
import { useIsNativeEmbed } from '../music24/navigation/hooks/useIsNativeEmbed';
import { detectMobileOs, openAppUrl } from './mobileAppLinks';
import styles from './AndroidAppBanner.module.scss';
import { BrandWord } from '../music24/components/primitives/BrandWord';

const DISMISSED_KEY = 'm24.androidAppBanner.dismissedAt';
const HIDE_FOR_MS = 30 * 24 * 60 * 60 * 1000;

function isDismissed(): boolean {
  try {
    const value = Number(window.localStorage.getItem(DISMISSED_KEY));
    return value > 0 && Date.now() - value < HIDE_FOR_MS;
  } catch {
    return false;
  }
}

/** True when the banner shows: Android browser, not the native app, not closed in the last 30 days. */
export function useAndroidAppBannerShown(): [boolean, () => void] {
  const isNativeEmbed = useIsNativeEmbed();
  const [isShown, setIsShown] = useState(
    () => !isNativeEmbed && detectMobileOs(navigator.userAgent) === 'android' && !isDismissed()
  );
  const dismiss = useCallback(() => {
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now()));
    } catch {
      // No storage: the banner comes back on the next visit.
    }
    setIsShown(false);
  }, []);
  return [isShown, dismiss];
}

interface AndroidAppBannerProps {
  onDismiss: () => void;
}

/**
 * Design music24-mobile-apps 1d/d2: a slim banner over the mobile web app on Android. iOS Safari
 * shows Apple's own Smart App Banner (`apple-itunes-app` in public/index.html) instead.
 */
const AndroidAppBannerComponent: React.FC<AndroidAppBannerProps> = ({ onDismiss }) => {
  const intl = useIntl();
  const href = useMemo(() => openAppUrl('android', window.location.host, config.nativeAppScheme, '/app/home'), []);

  return (
    <div className={styles.banner}>
      <button
        type="button"
        className={styles.close}
        onClick={onDismiss}
        aria-label={intl.formatMessage({ id: 'app.sml.mobileApps.banner.close', defaultMessage: 'Close' })}
      >
        <FontAwesomeIcon icon={faXmark} />
      </button>
      <span className={styles.logo}>
        <Music24Logo size={20} />
      </span>
      <span className={styles.text}>
        <b>
          <BrandWord />
        </b>
        <small>
          {intl.formatMessage({
            id: 'app.sml.mobileApps.banner.text',
            defaultMessage: 'Better in the app. Free on Google Play.',
          })}
        </small>
      </span>
      <a className={styles.open} href={href}>
        {intl.formatMessage({ id: 'app.sml.mobileApps.banner.open', defaultMessage: 'Open' })}
      </a>
    </div>
  );
};

export const AndroidAppBanner = TypedMemo(AndroidAppBannerComponent);
